import attachDocumentSizeMethods from 'lib/DocumentSize';
import ScreenSize from 'lib/ScreenSize';

export const LARGE = new ScreenSize({ maxWidth: Infinity, minWidth: 1200 });
export const MEDIUM = new ScreenSize({
  maxWidth: LARGE.minWidth - 1,
  minWidth: 992,
});
export const SMALL = new ScreenSize({
  maxWidth: MEDIUM.minWidth - 1,
  minWidth: 768,
});
export const X_SMALL = new ScreenSize({
  maxWidth: SMALL.minWidth - 1,
  minWidth: 0,
});

const CLOUDFRONT_IS_MOBILE_HEADER = 'cloudfront-is-mobile-viewer';
const CLOUDFRONT_IS_TABLET_HEADER = 'cloudfront-is-tablet-viewer';
const CLOUDFRONT_IS_DESKTOP_HEADER = 'cloudfront-is-desktop-viewer';

function userAgentIsMobile(userAgentString: string): boolean {
  const userAgentPrefix = userAgentString.substr(0, 4);
  const fullAgentNameMatch =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
  const prefixAgentNameMatch =
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i;

  return fullAgentNameMatch.test(userAgentString) || prefixAgentNameMatch.test(userAgentPrefix);
}

function getClientSideUserAgentString(): string {
  return typeof navigator !== 'undefined'
    ? navigator.userAgent || navigator.vendor || (global as any).opera
    : null;
}

class Headers {
  rawHeaders: Record<string, any>;

  constructor(rawHeaders: Record<string, any>) {
    this.rawHeaders = rawHeaders;
  }

  get isPhone(): boolean {
    return this.rawHeaders[CLOUDFRONT_IS_MOBILE_HEADER] === 'true' && !this.isTablet;
  }

  get isTablet(): boolean {
    return this.rawHeaders[CLOUDFRONT_IS_TABLET_HEADER] === 'true';
  }

  get isDesktop(): boolean {
    return this.rawHeaders[CLOUDFRONT_IS_DESKTOP_HEADER] === 'true';
  }

  get isMobile(): boolean | undefined {
    if (this.isDesktop) {
      return false;
    }

    if (this.isPhone || this.isTablet) {
      return true;
    }

    const userAgentString = this.rawHeaders['user-agent'];

    if (userAgentString) {
      return userAgentIsMobile(userAgentString);
    }

    return undefined;
  }
}

const DeviceScreen = {
  get windowExists(): boolean {
    return typeof window !== 'undefined';
  },

  getSize(): ScreenSize {
    if (this.isLarge()) {
      return LARGE;
    }
    if (this.isMedium()) {
      return MEDIUM;
    }
    if (this.isSmall()) {
      return SMALL;
    }
    if (this.isExtraSmall()) {
      return X_SMALL;
    }

    throw new Error('No size');
  },

  getWidth(): number | undefined {
    if (this.windowExists) {
      return (this as any).documentWidth() + this.scrollbarWidth();
    }
    return undefined;
  },

  getDeviceCategory(): string {
    if (this.isPhone()) {
      return 'mobile';
    }
    if (this.isTablet()) {
      return 'tablet';
    }
    return 'desktop';
  },

  scrollbarWidth(): number {
    return window.innerWidth - document.body.clientWidth;
  },

  isLarge(): boolean {
    const width = this.getWidth();
    return LARGE.includes(width);
  },

  isMedium(): boolean {
    const width = this.getWidth();
    return MEDIUM.includes(width);
  },

  isSmall(): boolean {
    const width = this.getWidth();
    return SMALL.includes(width);
  },

  isExtraSmall(): boolean {
    const width = this.getWidth();
    return X_SMALL.includes(width);
  },

  isBetween(_min: number, _max: number): boolean {
    const min = _min || X_SMALL.minWidth;
    const max = _max || LARGE.maxWidth;
    const width = this.getWidth();

    if (typeof width === 'undefined') return false;

    return width >= min && width <= max;
  },

  isAtMost(max: number): boolean {
    const width = this.getWidth();

    if (typeof width === 'undefined') return false;

    return width <= max;
  },

  isAtLeast(min: number | string): boolean {
    const width = this.getWidth();

    if (typeof width === 'undefined') return false;

    return width >= (typeof min === 'string' ? parseFloat(min as string) : min);
  },

  isMobile(headers = {}): boolean | undefined {
    if (new Headers(headers).isMobile) {
      return true;
    }

    const userAgentString = getClientSideUserAgentString();

    if (!userAgentString) {
      return undefined;
    }

    return userAgentIsMobile(userAgentString);
  },

  isPhone(headers = {}): boolean {
    if (new Headers(headers).isPhone) {
      return true;
    }

    return this.isExtraSmall();
  },

  isTablet(headers = {}): boolean {
    if (new Headers(headers).isTablet) {
      return true;
    }

    return Boolean(this.isMobile(headers) && !this.isExtraSmall());
  },

  isLandscape(): boolean {
    return window.innerWidth > window.innerHeight;
  },

  isPortrait(): boolean {
    return !this.isLandscape();
  },
};

// if in the browser, attach #documentHeight and #documentWidth
try {
  if (typeof window !== 'undefined') {
    attachDocumentSizeMethods(DeviceScreen);
  }
} catch (err) {
  console.error('error importing DeviceScreen', err);
}

export default DeviceScreen;
